"use client";

import { SubmitEvent, useCallback, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import clsx from "clsx";
import PinInput from "@/components/ui/forms/formPINInputComponent/formPINInput.component";
import type { PropriedadesDialogoAutenticacaoDoisFatores } from "./autenticacaoDoisFatores.types";
import { Alert } from "@mui/material";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import CampoUsuarioSpaceOtp from "./CampoUsuarioSpaceOtp";

// ------------------------------- CONSTANTS -------------------------------
const LOGO_GOOGLE_AUTHENTICATOR = "/assets/logos/logo-google-auth.svg";

// ------------------------------- COMPONENT -------------------------------
export default function DialogoAutenticacaoDoisFatores(props: Readonly<PropriedadesDialogoAutenticacaoDoisFatores>) {
	const {
		aberto,
		aoFechar,
		desabilitarFechamentoBackdrop = false,
		aoVerificar,
		carregando = false,
		mensagemErro,
		exibirCampoNomeUsuario = false,
		rotuloNomeUsuario = "Nome de usuário",
		placeholderNomeUsuario = "Digite o nome de usuário",
		permitirBuscaUsuarioPorCodigo = false,
		groupId,
		quantidadeDigitos = 6,
		titulo = "Autenticação em 2 fatores",
		subtitulo = "Abra o seu aplicativo do Google Authenticator para receber o código e se autenticar na plataforma",
		classe,
		conteudoAdicional,
		verificacaoDisponivel = true,
		identificacaoPersonalizada,
		aoCompletarCodigo,
		aoAlterarCodigo,
		revisaoCodigo = 0,
	} = props;

	// -------------------------------- STATES --------------------------------
	const [codigo, setCodigo] = useState("");
	const [nomeUsuario, setNomeUsuario] = useState<string | null>(null);

	// -------------------------------- EFFECTS -------------------------------
	useEffect(() => {
		setCodigo("");
		setNomeUsuario(null);
	}, [aberto, exibirCampoNomeUsuario]);

	useEffect(() => {
		setCodigo("");
	}, [revisaoCodigo, identificacaoPersonalizada?.chave]);

	// ----------------------------- DERIVED STATE ----------------------------
	const codigoCompleto = codigo.length === quantidadeDigitos;
	const podeInformarCodigo = identificacaoPersonalizada
		? identificacaoPersonalizada.disponivel
		: !exibirCampoNomeUsuario || nomeUsuario !== null;

	// -------------------------------- HANDLERS ------------------------------
	const validarUsuario = useCallback((nome: string | null) => {
		setNomeUsuario(nome);
		if (nome === null) setCodigo("");
	}, []);

	function alterarCodigo(valor: string): void {
		setCodigo(valor);
		aoAlterarCodigo?.(valor);
		if (valor.length === quantidadeDigitos && valor !== codigo && podeInformarCodigo && !carregando)
			aoCompletarCodigo?.(valor);
	}

	async function aoSubmeter(ev: SubmitEvent<HTMLFormElement>) {
		ev.preventDefault();

		if (!codigoCompleto || !podeInformarCodigo || !verificacaoDisponivel || carregando) return;
		await aoVerificar(codigo, exibirCampoNomeUsuario ? String(nomeUsuario ?? "") : undefined);
	}

	function aoFecharDialog(_: unknown, reason?: "backdropClick" | "escapeKeyDown") {
		if (carregando) return;
		if (desabilitarFechamentoBackdrop && reason === "backdropClick") return;

		setCodigo("");
		aoFechar?.();
	}

	// -------------------------------- RETURN --------------------------------
	return (
		<Dialog
			open={aberto}
			onClose={aoFecharDialog}
			fullWidth
			maxWidth="xs"
			slotProps={{
				paper: { sx: { mx: { xs: 1, sm: 4 }, width: { xs: "calc(100% - 16px)", sm: "calc(100% - 64px)" } } },
			}}
		>
			<DialogTitle className="flex justify-between">
				<div className="flex flex-col gap-4">
					<div className="mb-4 flex flex-col items-center justify-center">
						<img
							src={LOGO_GOOGLE_AUTHENTICATOR}
							alt="Google Authenticator"
							className={clsx("mb-1 h-24 w-24 object-contain", carregando && "opacity-70")}
						/>
						<h1 className="font-semibold">Google Authenticator</h1>
					</div>

					<div className="flex items-center gap-3">
						<h1 className="text-2xl">{titulo}</h1>
					</div>

					<p className="text-muted-foreground text-md">{subtitulo}</p>
				</div>
			</DialogTitle>

			<Box component="form" onSubmit={aoSubmeter} noValidate className="flex min-h-0 flex-1 flex-col">
				<DialogContent
					className={clsx(
						"min-h-0 flex-1 overflow-y-auto",
						"flex flex-col gap-1",
						conteudoAdicional ? "px-[clamp(2px,calc((100vw-316px)/2),24px)]!" : undefined,
						classe,
					)}
				>
					{identificacaoPersonalizada?.campo}
					{exibirCampoNomeUsuario ? (
						<CampoUsuarioSpaceOtp
							key={groupId ?? "padrao"}
							aberto={aberto}
							groupId={groupId}
							permitirBuscaUsuarioPorCodigo={permitirBuscaUsuarioPorCodigo}
							rotulo={rotuloNomeUsuario}
							placeholder={placeholderNomeUsuario}
							carregando={carregando}
							aoValidar={validarUsuario}
						/>
					) : null}

					{podeInformarCodigo ? (
						<PinInput
							length={quantidadeDigitos}
							value={codigo}
							onChange={alterarCodigo}
							masked
							autoComplete="one-time-code"
							autoFocus
							focusLastInput={Boolean(mensagemErro) && codigoCompleto}
							disabled={carregando}
							aria-label="Código de verificação"
							className="mt-3"
						/>
					) : null}

					{conteudoAdicional}

					{mensagemErro ? (
						<Alert severity="error" variant="standard" className="mt-4">
							{mensagemErro}
						</Alert>
					) : null}
				</DialogContent>

				<DialogActions className="shrink-0 justify-between px-6 pb-6">
					<div className="flex gap-2">
						{aoFechar ? (
							<Button
								variant="contained"
								disabled={carregando}
								onClick={() => aoFecharDialog(null)}
								color="error"
							>
								Fechar
							</Button>
						) : null}
					</div>
					<Button
						variant="contained"
						color="success"
						type="submit"
						disabled={!podeInformarCodigo || !codigoCompleto || !verificacaoDisponivel || carregando}
					>
						{carregando ? (
							<div className="items-center flex gap-2">
								<FuseSvgIcon size={18} className="animate-spin">
									lucide:loader
								</FuseSvgIcon>
								<span className="text-sm">Verificando...</span>
							</div>
						) : (
							<span className="text-sm">Verificar código</span>
						)}
					</Button>
				</DialogActions>
			</Box>
		</Dialog>
	);
}
